import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Wrench, CheckCircle, AlertCircle, Loader } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAuthHeaders } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      toast.error('Payment verification timed out');
      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}/api/payments/status/${sessionId}`,
        { headers: getAuthHeaders(), withCredentials: true }
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(response.data);
        toast.success('Payment successful!');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('error');
        toast.error('Payment session expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2 w-fit" data-testid="logo-link">
            <img src="/logo.png" alt="Mechanicease" className="h-14 w-auto" />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-lg mx-auto px-4 py-16">
        <Card className="bg-white border border-slate-200" data-testid="payment-status-card">
          <CardContent className="p-8 text-center">
            {status === 'checking' && (
              <>
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader className="w-8 h-8 text-blue-600 animate-spin" />
                </div>
                <h2 className="font-['Barlow'] text-2xl font-bold text-slate-900 mb-3">
                  Verifying Payment
                </h2>
                <p className="text-slate-600">
                  Please wait while we confirm your payment...
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="font-['Barlow'] text-2xl font-bold text-slate-900 mb-3">
                  Payment Successful!
                </h2>
                <p className="text-slate-600 mb-6">
                  Thank you for your payment. Your invoice has been marked as paid.
                </p>
                {paymentData && (
                  <div className="bg-slate-50 rounded-md p-4 mb-6 text-left">
                    <p className="text-sm text-slate-600">Amount Paid</p>
                    <p className="text-2xl font-bold text-green-600">
                      ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                    </p>
                  </div>
                )}
                <Button 
                  className="bg-orange-500 hover:bg-orange-600 text-white w-full"
                  onClick={() => navigate('/dashboard')}
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                </Button>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="font-['Barlow'] text-2xl font-bold text-slate-900 mb-3">
                  Payment Issue
                </h2>
                <p className="text-slate-600 mb-6">
                  We couldn't verify your payment. If you were charged, please contact support.
                </p>
                <div className="space-y-3">
                  <Button 
                    className="bg-orange-500 hover:bg-orange-600 text-white w-full"
                    onClick={() => navigate('/dashboard')}
                  >
                    Go to Dashboard
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full"
                    onClick={() => window.location.reload()}
                  >
                    Try Again
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PaymentSuccessPage;
